import { Base } from '@wilson/base';
import type { User } from '../accounts/User';
import type { Activity, ResolvedActivity } from '../activities/activity';
import { ActivityReport } from '../activities/activity-report';
import { OperationStatus } from '../activities/operationStatus';
import { Label } from '../labels/labels';
import { GeoLocation } from '../locations/geoLocation';
import { OrganizationalUnit } from '../organizational-units/organizationalUnit';
import { PayrollTransaction } from '../payroll/payroll-enums';
import { PayrollSettingCategory } from '../payroll/payroll-setting';
import { Project } from '../projects';
import { Sector } from '../sectors/sector';
import { ShiftSeriesV2 } from '../shift-series-v2/shift-series-v2';
import { WithTranslations } from '../translations';
import { WilsonApp } from '../wilson-app';
import { PublicationStatus, ShiftActivityStatus } from './publicationStatus';
import { ShiftClientComment } from './shift-client-comment';
import { ShiftReport } from './shift-report';
import { ShiftScore } from './shift-score';
import { ShiftState } from './shift-state';
import { ShiftSeriesShift } from './shiftSeries';
import { EntityLangType } from '@wilson/localization';

export interface Shift extends Base, Partial<WithPreparedAttributes> {
  activities?: Activity[] | null;
  comment: string | null;
  planningComment: string | null;
  createdFrom?: WilsonApp | null;
  deviation?: number;
  externalId?: string | null;
  name: string;
  organizationalUnit?: OrganizationalUnit | null;
  organizationalUnitId: string;
  payrollInformation?: ShiftPayrollInformation | null;
  publicationStatus: PublicationStatus;
  shiftCategory?: ShiftCategory | null;
  shiftCategoryId: string;
  shiftSeries?: ShiftSeriesShift | null;
  startDate: string;
  startDatetime?: string;
  endDatetime?: string;
  state: ShiftState;
  user?: User | null;
  userId: string | null;
  shiftScores?: ShiftScore[];
  shiftReport?: ShiftReport | null;
  shiftClientComments?: ShiftClientComment[] | null;
  excludeInValidation?: boolean;
  priority?: number | null;
  labels?: Label[] | null;
  projectId?: string | null;
  project?: Project | null;
  lastSeenAt?: string | null;
  confirmedAt?: string | null;
  declinedAt?: string | null;
  declineReason?: string | null;
  reopenReason?: string | null;
  reopenedAt?: string | null;
  reopenedByUserId?: string | null;
  reopenedByUser?: Pick<User, 'id' | 'firstName' | 'lastName'> | null;
}

export interface ShiftSeriesResolved extends Shift {
  shiftSeriesV2?: ShiftSeriesV2 | null;
}

export interface ShiftWithAzmAttributes extends Shift {
  agreementIds: string[];
  allActivitiesHaveAgreement: boolean;
  payrollTransactions: (PayrollTransaction & {
    payrollSettingCategory: PayrollSettingCategory | null;
  })[];
  plannedDuration: number;
  plannedReportedDeviationDuration: number;
  reportedDuration: number;
  shiftNotes: ShiftNotes[];
  shiftAttachments: Omit<ShiftAttachment, 'shiftId'>[];
}

export type ShiftWithAzmAttributesAndUser = ShiftWithAzmAttributes & {
  id: string;
  user: User;
};
export interface WithPreparedAttributes {
  startDatetime: string;
  endDatetime: string;
  deviatedStartDatetime: string | null;
  deviatedEndDatetime: string | null;
  reportedStartDatetime: string | null;
  reportedEndDatetime: string | null;
  startLocationId: string;
  endLocationId: string;
  startLocation: {
    locationCode: string;
    name: string;
  };
  endLocation: {
    locationCode: string;
    name: string;
  };
  reportedStartLocationId: string | null | undefined;
  reportedEndLocationId: string | null | undefined;
  reportedStartLocation:
    | {
        locationCode: string;
        name: string;
      }
    | null
    | undefined;
  reportedEndLocation:
    | {
        locationCode: string;
        name: string;
      }
    | null
    | undefined;
  operationalStatus: OperationStatus;
  priority: number | null;
  projectId: string | null;
  ignoreStayNecessary: boolean;
  sharePlanNumber: string | null;
  maintenanceNumber: string | null;
}

export interface ResolvedShift extends Shift {
  activities: Activity[];
  user: User;
  organizationalUnit: OrganizationalUnit;
}

export interface ResolvedShiftWithReports extends Shift {
  activities: ResolvedActivity[];
  user: User;
  organizationalUnit: OrganizationalUnit;
}

export interface ResolvedShiftWithActivity extends Shift {
  activities: ResolvedActivity[];
  user: User;
  organizationalUnit: OrganizationalUnit;
}

export interface ShiftWithActivityStatus extends ResolvedShiftWithActivity {
  activityStatus: ShiftActivityStatus;
}

export interface ShiftCategory extends Base {
  name: string;
  sectorIds: string[] | null;
  sectors?: Sector[];
}

export type ShiftWithActivitiesWithLocations = Omit<Shift, 'activities'> & {
  activities: (Activity & {
    startLocation: GeoLocation;
    endLocation: GeoLocation;
    activityReports: ActivityReport[];
  })[];
};

export enum ValidationCheckCategory {
  Qualifications = 'qualification',
  WorkingTimeRules = 'working-time-rule',
  ShiftLogic = 'shift-logic',
}

export enum ShiftCategoryNames {
  Disposition = 'shift_category.disposition',
  NonDepatureShift = 'shift_category.non-depature-shift',
  Normal = 'shift_category.normal',
  Office = 'shift_category.office',
  OnCall = 'shift_category.on-call',
  SickShift = 'shift_category.sick-shift',
  Training = 'shift_category.training',
  HomeTransfer = 'shift_category.home-transfer',
  Briefing = 'shift_category.briefing',
  AdHoc = 'shift_category.ad-hoc',
}

export enum ShiftCategoryEnum {
  Disposition = 'disposition',
  NonDepatureShift = 'non-depature-shift',
  Normal = 'normal',
  Office = 'office',
  OnCall = 'on-call',
  SickShift = 'sick-shift',
  Training = 'training',
  HomeTransfer = 'home-transfer',
  Briefing = 'briefing',
  AdHoc = 'ad-hoc',
}

export enum ShiftCategoriesIdsMap {
  Disposition = 'bc38ab77-04f2-4db4-92c4-bc4f0b18aeec',
  NonDepatureShift = 'd9d55e2c-2ddc-4a77-89b4-95676045d828',
  Normal = 'e9863d0b-1af1-48f0-be13-7b6245b7e638',
  Office = 'd5b7ac96-74b7-495f-8766-a559d89acc25',
  OnCall = '214a0f6b-dbbe-4da6-9a6f-d646281cd05f',
  SickShift = '74581926-2b1a-41f6-9bae-204716fc31a1',
  Training = '93857c9a-899a-4020-ad08-f234d6f6615c',
  HomeTransfer = 'bdf6bcf2-c6f9-4e29-baeb-b3d7122704f3',
  Briefing = '07dc8b1e-4c20-4d2b-b5c2-a4513910fb6d',
  AdHoc = 'aafebd72-a25d-4a15-bf55-03d9d26d431e',
}

export const ShiftCategoriesIdToNameMap: Record<string, ShiftCategoryNames> = {
  'e9863d0b-1af1-48f0-be13-7b6245b7e638': ShiftCategoryNames.Normal,
  'bc38ab77-04f2-4db4-92c4-bc4f0b18aeec': ShiftCategoryNames.Disposition,
  '93857c9a-899a-4020-ad08-f234d6f6615c': ShiftCategoryNames.Training,
  'd5b7ac96-74b7-495f-8766-a559d89acc25': ShiftCategoryNames.Office,
  '214a0f6b-dbbe-4da6-9a6f-d646281cd05f': ShiftCategoryNames.OnCall,
  '74581926-2b1a-41f6-9bae-204716fc31a1': ShiftCategoryNames.SickShift,
  'd9d55e2c-2ddc-4a77-89b4-95676045d828': ShiftCategoryNames.NonDepatureShift,
  'bdf6bcf2-c6f9-4e29-baeb-b3d7122704f3': ShiftCategoryNames.HomeTransfer,
  '07dc8b1e-4c20-4d2b-b5c2-a4513910fb6d': ShiftCategoryNames.Briefing,
  'aafebd72-a25d-4a15-bf55-03d9d26d431e': ShiftCategoryNames.AdHoc,
};

export const ShiftCategoriesColorCode: Record<string, string> = {
  'e9863d0b-1af1-48f0-be13-7b6245b7e638': 'var(--grey-6)',
  'bc38ab77-04f2-4db4-92c4-bc4f0b18aeec': 'var(--gold-5)',
  '93857c9a-899a-4020-ad08-f234d6f6615c': 'var(--magenta-4)',
  'd5b7ac96-74b7-495f-8766-a559d89acc25': 'var(--blue-5)',
  '214a0f6b-dbbe-4da6-9a6f-d646281cd05f': 'var(--lime-5)',
  '74581926-2b1a-41f6-9bae-204716fc31a1': 'var(--red-4)',
  'd9d55e2c-2ddc-4a77-89b4-95676045d828': 'var(--orange-5)',
  'bdf6bcf2-c6f9-4e29-baeb-b3d7122704f3': 'var(--cyan-5)',
  '07dc8b1e-4c20-4d2b-b5c2-a4513910fb6d': 'var(--purple-5)',
  'aafebd72-a25d-4a15-bf55-03d9d26d431e': 'var(--geekblue-5)',
};

export const ShiftCardBackgroundColor: Record<string, string> = {
  'e9863d0b-1af1-48f0-be13-7b6245b7e638': '#bfbfbf33',
  'bc38ab77-04f2-4db4-92c4-bc4f0b18aeec': '#FFD66633',
  '93857c9a-899a-4020-ad08-f234d6f6615c': '#FF85C033',
  'd5b7ac96-74b7-495f-8766-a559d89acc25': '#40A9FF33',
  '214a0f6b-dbbe-4da6-9a6f-d646281cd05f': '#bae63733',
  '74581926-2b1a-41f6-9bae-204716fc31a1': '#ff787533',
  'd9d55e2c-2ddc-4a77-89b4-95676045d828': '#ffa94033',
  'bdf6bcf2-c6f9-4e29-baeb-b3d7122704f3': '#36cfc933',
  '07dc8b1e-4c20-4d2b-b5c2-a4513910fb6d': '#9254de33',
  'aafebd72-a25d-4a15-bf55-03d9d26d431e': '#597ef733',
};

export interface ShiftValidationDetails {
  category: ValidationCheckCategory;
  checkName: string;
  localizedCheckNames?: Record<EntityLangType, string>;
  valid: boolean;
  errorCausingObjectIds: number[];
}

export interface DeviationRange {
  from: number;
  to: number;
}

export interface ShiftInDateRangeDto {
  startDate: string;
  endDate: string;
  organizationalUnitId?: string[];
  userId?: string | string[];
}

export interface ShiftPayrollInformation extends Base {
  travelKM: number;
  spontaneousShiftBonus: boolean;
  shiftId: string;
  traineeOnBoard: boolean;
  coveredFoodExpenses: CoveredFoodExpenseEnum[];
  shift?: Shift;
  overnightStay: boolean;
}

export interface ShiftNotes {
  id: string;
  createdByUserId: string;
  note: string;
  shiftId: string;
  createdAt: string;
}

export enum ShiftExternalWorkflowType {
  IMPORT = 'import',
  SHIFT_SELECTION_ACTION = 'shift_selection_action',
}

export interface ShiftExternalWorkflow extends WithTranslations {
  id: string;
  type: ShiftExternalWorkflowType;
  url: string;
  name: string;
  organizationalUnit: OrganizationalUnit;
  organizationalUnitId: OrganizationalUnit['id'];
}

export const ALL_SHIFT_RELATIONS = [
  `activities.activityCategory`,
  `activities.agreement`,
  `activities.agreement.client`,
  `activities.activityReports.location`,
  `activities.activityReports`,
  `activities.activityComments`,
  `activities.activityComments.sender`,
  `activities.job`,
  `activities.job.organizationalUnit`,
  `activities.endLocation`,
  `activities.service`,
  `activities.startLocation`,
  `activities`,
  `organizationalUnit`,
  `payrollInformation`,
  `shiftCategory`,
  `shiftSeries`,
  `user`,
  `reopenedByUser`,
  'shiftScores',
];

export const SHIFT_REPORT_CLIENT_COMMENT_FEATURE_FLAG =
  'shift-report-client-comment';

export const SHIFT_CLIENT_COMMENTS_RELATIONS = [
  'shiftClientComments',
  'shiftClientComments.client',
];

export const PREPARED_SHIFT = 'preparedShift';
export const PREPARED_SHIFT_SERIES = 'preparedShiftSeries';
export const PREPARED_SHIFT_VIA_AI = 'preparedShiftViaAi';

export enum CoveredFoodExpenseEnum {
  Breakfast = 'breakfast',
  Lunch = 'lunch',
  Dinner = 'dinner',
}

export interface ShiftAttachment {
  id: string;
  fileName: string;
  attachmentType: ShiftAttachmentType;
  shiftId: string;
  shift: { name: string };
  sizeInBytes: number;
  createdAt: string;
  ownerId: string;
  fileSignature: string | null;
}

export interface ShiftAttachmentDetails {
  name: string;
  shiftId: string;
  attachmentId: string;
}

export interface ListRowOfAttachmentWithMultipleShiftIds
  extends Pick<
    ShiftAttachment,
    'fileName' | 'sizeInBytes' | 'createdAt' | 'attachmentType'
  > {
  id: string;
  shiftDetails: ShiftAttachmentDetails[];
}

export enum ShiftAttachmentType {
  Other = 'OTHER',
  Report = 'REPORT',
}
